/**
 * AGENT-BC2: "Quotes Issued vs Quotes Won" line chart.
 *
 * Mirrors the PBI HSQ-page bookmark navigator `44c152c9d828fd685c9d` theme.
 * No existing PBI combo visual maps cleanly to this pairing (every candidate
 * bundles Revenue as a bar series and pairs Quotes Issued with Quotes
 * Converted rather than Quotes Won). The backend synthesises this payload
 * from `IA_QuotesIssued_Dynamic` + `IA_QuotesWon_Dynamic` aggregated by year.
 *
 * Both series are counts → single y-axis using `formatCount`. Visual fidelity
 * (circle markers, faint area gradient, rich tooltip) ships through
 * `_chartUtils.ts` so all four bottom-strip charts read as one design system.
 *
 * Reads `reportData.quotesOrdersChart` from the shared `useReportData` hook.
 */

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { dashboardChartTheme } from "../tokens";
import {
  AXIS_LABEL_FONT_SIZE,
  buildLineSeries,
  cssVar,
  decorateLineSeries,
  formatCount,
  hexToRgba,
  LINE_PALETTE_ORANGE,
  makeCountAndPercentTooltipFormatter,
  makeTooltipBlock,
  STANDARD_GRID,
  STANDARD_LEGEND,
} from "./_chartUtils";
import {
  sanitizeXAxis,
  useReportData,
  type ComboChartPayload,
  type ComboSeries,
} from "./useReportData";
import { useUiStore } from "../state/uiStore";

const CHART_ID = "quotesOrders";
const FALLBACK_TITLE = "Quotes Issued vs Quotes Won";

const AREA_OPACITY = 0.2;
// "% Won" derived line — green = win-rate, on the right-hand % axis. Win-rate
// green has no --dash-series-N equivalent, so it stays a semantic literal.
const WIN_PCT_COLOR = "#16a34a";
const WIN_PCT_SERIES_NAME = "% Won";

export function QuotesOrdersChart(): JSX.Element {
  const { data, isLoading, isError } = useReportData();
  // Re-read the dark/light CSS vars when the theme flips.
  const theme = useUiStore((s) => s.theme);

  const payload: ComboChartPayload | undefined = data?.quotesOrdersChart;
  const xAxisData = sanitizeXAxis(payload?.xAxis, CHART_ID);
  const series: ComboSeries[] = payload?.series ?? [];
  const title = (payload?.title && payload.title.trim()) || FALLBACK_TITLE;

  const hasData = xAxisData.length > 0 && series.length > 0;

  const option = useMemo(() => {
    if (!hasData) return null;

    const decorated = decorateLineSeries(series, LINE_PALETTE_ORANGE);
    const builtSeries = buildLineSeries(series, decorated);
    // Colors from the shared --dash-series-N tokens so they adapt to dark mode
    // (light values equal the original orange/pink spec hex). Read here, inside
    // the theme-keyed memo, so a theme flip re-resolves them.
    const seriesColors = [
      cssVar("--dash-series-3", "#ff8c42"), // Quotes Issued — orange
      cssVar("--dash-series-5", "#e91e8c"), // Quotes Won — pink
    ];
    const mappedSeries = builtSeries.map((s: any, i: number) => {
      const color = seriesColors[i % seriesColors.length];
      return {
        ...s,
        lineStyle: { ...(s.lineStyle ?? {}), color },
        itemStyle: { ...(s.itemStyle ?? {}), color },
        areaStyle: { color: hexToRgba(color, AREA_OPACITY) },
      };
    });

    // Derive "% Won" per quarter = Quotes Won / Quotes Issued * 100, plotted
    // on a secondary right-hand axis so the conversion rate reads alongside
    // the volume lines. Quarters with no quotes issued show 0 (no division).
    const issuedData =
      series.find((s) => /issued/i.test(s.name))?.data ?? [];
    const wonData =
      series.find((s) => /won/i.test(s.name))?.data ?? [];
    const pctData = xAxisData.map((_, i) => {
      const iss = Number(issuedData[i]) || 0;
      const won = Number(wonData[i]) || 0;
      return iss > 0 ? Math.round((won / iss) * 1000) / 10 : 0;
    });
    const pctSeries = {
      name: WIN_PCT_SERIES_NAME,
      type: "line" as const,
      yAxisIndex: 1,
      data: pctData,
      smooth: false,
      symbol: "circle",
      symbolSize: 5,
      lineStyle: { color: WIN_PCT_COLOR, width: 2, type: "dashed" as const },
      itemStyle: { color: WIN_PCT_COLOR },
      z: 5,
    };

    const axisNameStyle = {
      fontSize: AXIS_LABEL_FONT_SIZE,
      color: cssVar("--dash-chart-axis-label", "#5a6473"),
      fontWeight: 500,
    };

    return dashboardChartTheme({
      legend: STANDARD_LEGEND,
      grid: STANDARD_GRID,
      tooltip: makeTooltipBlock(
        makeCountAndPercentTooltipFormatter(new Set([WIN_PCT_SERIES_NAME])),
      ),
      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: false,
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE },
      },
      yAxis: [
        {
          type: "value",
          name: "Quotes",
          nameLocation: "middle" as const,
          nameGap: 40,
          nameTextStyle: axisNameStyle,
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCount(v),
          },
          splitLine: { show: true, lineStyle: { type: "dotted" as const } },
        },
        {
          type: "value",
          name: "% Won",
          position: "right" as const,
          min: 0,
          max: 100,
          nameLocation: "middle" as const,
          nameGap: 40,
          nameTextStyle: axisNameStyle,
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => `${v}%`,
          },
          splitLine: { show: false },
        },
      ],
      series: [...mappedSeries, pctSeries],
    });
  }, [hasData, series, xAxisData, theme]);

  return (
    <div className="dash-card">
      <h3 className="charts-row__title">{title}</h3>
      {isLoading ? (
        <div className="charts-row__empty charts-row__empty--shimmer" aria-busy="true">
          <span className="charts-row__shimmer" />
        </div>
      ) : isError ? (
        <div className="charts-row__empty charts-row__empty--message">Failed to load chart data</div>
      ) : !hasData || !option ? (
        <div className="charts-row__empty charts-row__empty--message">No chart data available</div>
      ) : (
        <ReactECharts
          className="charts-row__chart"
          option={option}
          notMerge
          lazyUpdate
          style={{ height: "100%", width: "100%" }}
        />
      )}
    </div>
  );
}

export default QuotesOrdersChart;
